/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

/**
 * Web PubSub transport implementing the AHP {@link AhpTransport} interface for
 * the browser.
 *
 * Connects to Azure Web PubSub using the reliable JSON subprotocol and
 * translates between AHP JSON-RPC messages and WPS `sendToGroup` /
 * group-message framing via the {@link buildPublish} / {@link parseInbound}
 * adapters. Token refresh is the caller's responsibility — when a token
 * expires, close the old transport and create a new one with a fresh token.
 */

import type {AhpTransport, JsonRpcMessage, TransportFrame} from '@microsoft/agent-host-protocol/client'
import {TransportError} from '@microsoft/agent-host-protocol/client'
import {ACCEPTS_BATCH, DEFAULT_MAX_REASSEMBLY_BYTES, Reassembler} from './chunking'
import {buildCapabilityDeclaration, buildPublish, parseInbound, RELIABLE_JSON_SUBPROTOCOL} from './framing'
import type {ParseGroupNameOptions} from './groups'

/** Message event delivered to {@link WebSocketLike.onmessage}. */
export interface WpsMessageEvent {
  data: unknown
}

/** Close event delivered to {@link WebSocketLike.onclose}. */
export interface WpsCloseEvent {
  code: number
  reason: string
}

/**
 * Minimal structural subset of the browser WebSocket interface this transport
 * depends on, so a fake socket can be injected in tests via
 * {@link WpsTransportOptions.webSocketFactory}.
 */
export interface WebSocketLike {
  send(data: string): void
  close(): void
  onopen: (() => void) | null
  onmessage: ((event: WpsMessageEvent) => void) | null
  onclose: ((event: WpsCloseEvent) => void) | null
  onerror: ((event: unknown) => void) | null
}

/** Opens a {@link WebSocketLike} for `url` negotiating `subprotocol`. */
export type WebSocketFactory = (url: string, subprotocol: string) => WebSocketLike

/** How often to sweep the reassembler for abandoned partial-chunk buffers. */
const REASSEMBLY_SWEEP_INTERVAL_MS = 15_000

/** Upper bound on the WPS handshake (socket open → `connected` → all joinGroup acks). */
const WPS_HANDSHAKE_TIMEOUT_MS = 30_000

// The SDK restarts request IDs on each new client. Keep wire IDs distinct so
// delayed replies on a reused relay lane cannot answer a replacement's requests.
let nextWireRequestId = Math.floor(Math.random() * 2 ** 52)
const MAX_PENDING_REQUESTS = 1024
const MAX_PENDING_MESSAGES = 1024

const inboundDecoder = new TextDecoder('utf-8')
const messageEncoder = new TextEncoder()

/**
 * Coerce an inbound WebSocket message payload to a string. WPS reliable JSON
 * frames are text, but `Buffer`/`ArrayBuffer`/typed-array payloads are
 * tolerated defensively.
 */
function frameDataToString(data: unknown): string {
  if (typeof data === 'string') return data
  if (data instanceof ArrayBuffer) return inboundDecoder.decode(data)
  if (ArrayBuffer.isView(data)) return inboundDecoder.decode(data)
  return String(data)
}

/** Default factory: a real browser WebSocket exposing the event API. */
const defaultWebSocketFactory: WebSocketFactory = (url, subprotocol) =>
  new WebSocket(url, subprotocol) as unknown as WebSocketLike

export interface WpsTransportOptions {
  /** Full WebSocket URL (including access_token query param). */
  url: string
  /** Group to publish outbound AHP messages to (the `to-host` lane). */
  toHostGroup: string
  /** Groups to join on connect (broadcast + to-client, and optionally root). */
  joinGroups: string[]
  /** Forwarded to parseInbound for uid/eid pinning. */
  groupValidation?: ParseGroupNameOptions
  /** Opens the underlying WebSocket. Defaults to a real browser socket. */
  webSocketFactory?: WebSocketFactory
  /** Invoked when an inbound frame can't be parsed or framed. */
  onProtocolError?: (err: unknown) => void
  /** Observes decoded inbound AHP messages before the client consumes them. */
  onMessage?: (message: JsonRpcMessage) => void
  /** Reports an unexpected established-socket disconnect before recv() observes it. */
  onDisconnect?: (closeCode: number) => void
  /**
   * Invoked once when the AHP connection behind the lane ends while the socket
   * stays open, before `recv()` reports the close.
   */
  onConnectionEnded?: (reason: WpsConnectionEndReason) => void
}

/**
 * Why the AHP connection behind a relay lane ended while its socket stayed open:
 * the host's `closed` notice, a later host connection answering a current
 * request because that notice was lost, or {@link WpsTransport.endConnection}.
 */
export type WpsConnectionEndReason = 'host-closed' | 'host-replaced' | 'unresponsive'

const CONNECTION_END_MESSAGES: Record<WpsConnectionEndReason, string> = {
  'host-closed': 'AHP host closed the relay connection',
  'host-replaced': 'AHP host replaced the relay connection',
  unresponsive: 'AHP host stopped responding on the relay connection',
}

/**
 * AHP transport over the Web PubSub reliable JSON subprotocol.
 *
 * 1. `WpsTransport.connect(options)` opens the WebSocket.
 * 2. The transport auto-joins the requested groups on the WPS `connected` event.
 * 3. Use `send()` / `recv()` as a normal AHP transport.
 * 4. `close()` tears down.
 */
export class WpsTransport implements AhpTransport {
  private ws: WebSocketLike
  private readonly toHostGroup: string
  private readonly joinGroups: string[]
  private readonly reassembler = new Reassembler()
  private readonly groupValidation?: ParseGroupNameOptions
  private readonly onProtocolError?: (err: unknown) => void
  private readonly onMessage?: (message: JsonRpcMessage) => void
  private readonly onDisconnect?: (closeCode: number) => void
  readonly #onConnectionEnded?: (reason: WpsConnectionEndReason) => void
  private ackId = 0
  private lastReceivedSequenceId = 0
  private sweepTimer: ReturnType<typeof setInterval> | null = null

  private inbox: Array<TransportFrame | null> = []
  private waiter: ((frame: TransportFrame | null) => void) | null = null
  private closed = false
  #generation: number | undefined
  #handshakeId: number | undefined
  #handshakeAnswered = false
  readonly #pendingRequests = new Map<number, number>()
  readonly #closedGenerations = new Set<number | undefined>()
  #pendingMessages: Array<{message: JsonRpcMessage; generation: number}> = []
  #pendingMessageBytes = 0
  private readonly pendingJoinAcks = new Map<number, string>()
  private resolveConnect: ((t: WpsTransport) => void) | null = null
  private rejectConnect: ((err: Error) => void) | null = null
  private connectTimer: ReturnType<typeof setTimeout> | null = null
  private resolved = false

  private constructor(ws: WebSocketLike, options: WpsTransportOptions) {
    this.ws = ws
    this.toHostGroup = options.toHostGroup
    this.joinGroups = options.joinGroups
    this.groupValidation = options.groupValidation
    this.onProtocolError = options.onProtocolError
    this.onMessage = options.onMessage
    this.onDisconnect = options.onDisconnect
    this.#onConnectionEnded = options.onConnectionEnded
  }

  /** Open a WPS WebSocket connection with the reliable JSON subprotocol. */
  static connect(options: WpsTransportOptions): Promise<WpsTransport> {
    return new Promise((resolve, reject) => {
      const factory = options.webSocketFactory ?? defaultWebSocketFactory
      const ws = factory(options.url, RELIABLE_JSON_SUBPROTOCOL)

      const transport = new WpsTransport(ws, options)
      transport.rejectConnect = reject
      transport.connectTimer = setTimeout(() => {
        transport.failConnection(new TransportError('io', 'WPS handshake timed out'))
      }, WPS_HANDSHAKE_TIMEOUT_MS)

      ws.onopen = () => {
        // WPS reliable JSON sends a `connected` system message after open; we
        // wait for it before resolving.
      }

      ws.onmessage = event => {
        try {
          const frame = JSON.parse(frameDataToString(event.data)) as Record<string, unknown>
          transport.handleFrame(frame, resolve)
        } catch (err) {
          transport.onProtocolError?.(err)
        }
      }

      ws.onerror = () => {
        transport.failConnection(new TransportError('io', 'WebSocket error during WPS connect'))
      }

      ws.onclose = ev => {
        if (!transport.closed) {
          transport.closed = true
          transport.#clearPending()
          transport.clearConnectTimer()
          transport.deliver(null)
          reject(new TransportError('closed', `WebSocket closed: ${ev.code} ${ev.reason}`))
        }
      }
    })
  }

  private handleFrame(frame: Record<string, unknown>, resolve?: (t: WpsTransport) => void): void {
    if (this.closed) return

    if (frame['type'] === 'system' && frame['event'] === 'connected') {
      if (!this.resolved && resolve) {
        this.resolved = true
        this.resolveConnect = resolve
        for (const group of this.joinGroups) {
          const ackId = ++this.ackId
          this.pendingJoinAcks.set(ackId, group)
          this.sendRaw({type: 'joinGroup', group, ackId})
        }
        if (this.pendingJoinAcks.size === 0) {
          this.finishConnect()
        }
      }
      return
    }

    if (frame['type'] === 'ack') {
      const ackId = typeof frame['ackId'] === 'number' ? frame['ackId'] : undefined
      if (ackId === undefined || !this.pendingJoinAcks.has(ackId)) {
        return
      }
      const group = this.pendingJoinAcks.get(ackId)
      this.pendingJoinAcks.delete(ackId)
      if (frame['success'] === false) {
        this.failConnection(new TransportError('io', `WPS joinGroup failed for group '${group}'`))
        return
      }
      if (this.pendingJoinAcks.size === 0) {
        this.finishConnect()
      }
      return
    }

    if (frame['type'] === 'message' && frame['sequenceId'] !== undefined) {
      const sequenceId = frame['sequenceId']
      if (typeof sequenceId !== 'number' || !Number.isSafeInteger(sequenceId) || sequenceId <= 0) {
        throw new TransportError('protocol', 'WPS message sequenceId must be a positive safe integer')
      }
      const highestSequenceId = Math.max(this.lastReceivedSequenceId, sequenceId)
      // Receipt acks free Azure's 1000-message / 16 MB replay buffer. Send them
      // before parsing or reassembly, including for redeliveries we will drop.
      try {
        this.sendRaw({type: 'sequenceAck', sequenceId: highestSequenceId})
      } catch (cause) {
        const error = new TransportError('io', 'WPS sequence acknowledgment failed', {cause})
        this.failConnection(error)
        throw error
      }
      if (sequenceId <= this.lastReceivedSequenceId) return
      this.lastReceivedSequenceId = sequenceId
    }

    const result = parseInbound(frame, {
      reassembler: this.reassembler,
      groupValidation: this.groupValidation,
    })

    if (result.kind === 'payload') {
      this.deliverMessage(result.payload, result.generation)
    } else if (result.kind === 'payloads') {
      for (const payload of result.payloads) {
        this.deliverMessage(payload, result.generation)
      }
    } else if (result.kind === 'closed') {
      if (!this.#handshakeAnswered) {
        // A delayed handshake reply can follow its connection's closure.
        // Defer the notice until the reply identifies which connection ended.
        if (this.#closedGenerations.size >= MAX_PENDING_REQUESTS) {
          this.failConnection(new TransportError('closed', 'WPS pending closure limit exceeded'))
        } else {
          this.#closedGenerations.add(result.generation)
        }
      } else if (
        result.generation === undefined ||
        this.#generation === undefined ||
        result.generation === this.#generation
      ) {
        this.#endConnection('host-closed')
      }
    }
  }

  private deliverMessage(payload: unknown, generation?: number): void {
    if (this.closed) return
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
      // The AHP client closes its connection on a message like this, and
      // observers assume an object. Skip it so the rest of a batch still arrives.
      this.onProtocolError?.(new TransportError('protocol', 'WPS message payload must be a JSON object'))
      return
    }
    let message = payload as JsonRpcMessage
    const responseId = message && !('method' in message) && 'id' in message ? message.id : undefined
    const originalId = responseId === undefined ? undefined : this.#pendingRequests.get(responseId)
    // Only a request sent on this transport can be answered on it.
    if (responseId !== undefined && originalId === undefined) return
    const isHandshakeResponse = responseId !== undefined && responseId === this.#handshakeId && originalId !== undefined
    if (isHandshakeResponse) {
      this.#generation = generation
      this.#handshakeAnswered = true
      this.#handshakeId = undefined
    } else if (generation !== undefined && !this.#handshakeAnswered) {
      // Actions can arrive between chunks of the handshake snapshot. Wait for
      // its generation before deciding which connection's messages to deliver.
      const bytes = messageEncoder.encode(JSON.stringify(message)).byteLength
      if (
        this.#pendingMessages.length >= MAX_PENDING_MESSAGES ||
        this.#pendingMessageBytes + bytes > DEFAULT_MAX_REASSEMBLY_BYTES
      ) {
        const error = new TransportError('io', 'WPS pending handshake message limit exceeded')
        this.failConnection(error)
        throw error
      }
      this.#pendingMessages.push({message, generation})
      this.#pendingMessageBytes += bytes
      return
    } else if (generation !== undefined && generation !== this.#generation) {
      // A successor answering a current request proves the host connection
      // ended even if its closure notice was lost. Never deliver that reply.
      if (originalId !== undefined) this.#endConnection('host-replaced')
      return
    }
    if (responseId !== undefined && originalId !== undefined) {
      this.#pendingRequests.delete(responseId)
      message = {...message, id: originalId}
    }
    this.onMessage?.(message)
    this.deliver({kind: 'text', text: JSON.stringify(message)})
    if (isHandshakeResponse) {
      const pendingMessages = this.#pendingMessages
      this.#pendingMessages = []
      this.#pendingMessageBytes = 0
      for (const pending of pendingMessages) {
        this.deliverMessage(pending.message, pending.generation)
      }
      if (this.#closedGenerations.size > 0) {
        const ended = this.#closedGenerations.has(undefined) || this.#closedGenerations.has(this.#generation)
        this.#closedGenerations.clear()
        if (ended) this.#endConnection('host-closed')
      }
    }
  }

  /** Called once all joinGroup acks have arrived. */
  private finishConnect(): void {
    if (!this.resolveConnect) return
    const resolve = this.resolveConnect
    this.resolveConnect = null
    this.rejectConnect = null
    this.clearConnectTimer()
    this.sweepTimer = setInterval(() => {
      this.reassembler.sweepExpired()
    }, REASSEMBLY_SWEEP_INTERVAL_MS)
    this.ws.onmessage = event => {
      try {
        const f = JSON.parse(frameDataToString(event.data)) as Record<string, unknown>
        this.handleFrame(f)
      } catch (err) {
        this.onProtocolError?.(err)
      }
    }
    this.ws.onclose = event => {
      if (!this.closed) {
        this.onDisconnect?.(event.code)
        this.closed = true
        this.stopSweep()
        this.#clearPending()
        this.deliver(null)
      }
    }
    this.ws.onerror = () => {
      if (!this.closed) {
        this.onDisconnect?.(1006)
        this.closed = true
        this.stopSweep()
        this.#clearPending()
        this.deliver(null)
      }
    }
    resolve(this)
  }

  /** Close a failed transport and reject the handshake if it is still pending. */
  private failConnection(err: Error): void {
    if (this.closed) return
    this.closed = true
    const reject = this.rejectConnect
    this.rejectConnect = null
    this.resolveConnect = null
    this.clearConnectTimer()
    this.stopSweep()
    this.#clearPending()
    this.deliver(null)
    try {
      this.ws.close()
    } catch {
      // best-effort
    }
    reject?.(err)
  }

  /**
   * End the AHP connection behind the lane as the host's `closed` notice
   * would. For a lane that stopped delivering traffic the client was owed:
   * the notice is a best-effort publish, and a later host connection answers
   * `ping` before any handshake, so the socket staying open proves nothing.
   */
  endConnection(): void {
    this.#endConnection('unresponsive')
  }

  #endConnection(reason: WpsConnectionEndReason): void {
    if (this.closed) return
    this.failConnection(new TransportError('closed', CONNECTION_END_MESSAGES[reason]))
    this.#onConnectionEnded?.(reason)
  }

  private clearConnectTimer(): void {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
    }
  }

  #clearPending(): void {
    this.#pendingRequests.clear()
    this.#closedGenerations.clear()
    this.#pendingMessages = []
    this.#pendingMessageBytes = 0
  }

  private stopSweep(): void {
    if (this.sweepTimer) {
      clearInterval(this.sweepTimer)
      this.sweepTimer = null
    }
  }

  send(message: JsonRpcMessage | string): void {
    if (this.closed) {
      throw new TransportError('closed', 'transport closed')
    }
    let payload: JsonRpcMessage = typeof message === 'string' ? JSON.parse(message) : message
    if ('method' in payload && 'id' in payload) {
      if (this.#pendingRequests.size >= MAX_PENDING_REQUESTS || !Number.isSafeInteger(nextWireRequestId + 1)) {
        const error = new TransportError('io', 'WPS outstanding request limit exceeded')
        this.failConnection(error)
        throw error
      }
      const wireId = ++nextWireRequestId
      this.#pendingRequests.set(wireId, payload.id)
      if (payload.method === 'initialize' || payload.method === 'reconnect') {
        this.#handshakeId = wireId
        this.#handshakeAnswered = false
      }
      payload = {...payload, id: wireId}
    }
    const frames = buildPublish({
      group: this.toHostGroup,
      nextAckId: () => ++this.ackId,
      payload,
    })
    for (const frame of frames) {
      this.sendRaw(frame)
    }
  }

  /**
   * Declare batch-envelope support after the AHP initialize/reconnect result.
   *
   * This is transport control rather than AHP JSON-RPC, so it is published
   * directly instead of passing through {@link send}.
   */
  declareBatchSupport(): void {
    if (this.closed) {
      throw new TransportError('closed', 'transport closed')
    }
    this.sendRaw(buildCapabilityDeclaration(this.toHostGroup, () => ++this.ackId, [ACCEPTS_BATCH]))
  }

  recv(): Promise<TransportFrame | null> {
    if (this.inbox.length > 0) {
      return Promise.resolve(this.inbox.shift() ?? null)
    }
    if (this.closed) {
      return Promise.resolve(null)
    }
    return new Promise(resolve => {
      this.waiter = resolve
    })
  }

  close(): Promise<void> {
    if (this.closed) return Promise.resolve()
    this.closed = true
    this.stopSweep()
    this.#clearPending()
    this.deliver(null)
    this.ws.close()
    return Promise.resolve()
  }

  private deliver(frame: TransportFrame | null): void {
    if (this.waiter) {
      const w = this.waiter
      this.waiter = null
      w(frame)
    } else {
      this.inbox.push(frame)
    }
  }

  private sendRaw(obj: unknown): void {
    this.ws.send(JSON.stringify(obj))
  }
}
